import React, { useState, useEffect, useCallback, useMemo } from "react";
import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import VirtualizedCardGrid from "../layouts/VirtualizedCardGrid";
import AppSelectFilter from "../components/common/AppSelectFilter";
import BranchSelectFilter from "../components/common/BranchSelectFilter";
import TeacherSelectFilter from "../components/common/TeacherSelectFilter";
import SubjectSelectFilter from "../components/common/SubjectSelectFilter";
import TimetableSlotCard from "../components/timetableSlots/TimetableSlotCard";
import UpdateTimetableSlotModal from "../components/timetableSlots/UpdateTimetableSlotModal";

import { usePermissions } from "../hooks/usePermissions";
import useDebounce from "../hooks/useDebounce";

import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getTimetableSlots,
  resetTimetableSlotServerStatus,
} from "../redux/actions/timetableSlotActions";
import { getTimetables } from "../redux/actions/timetableActions";
import { getTeachers } from "../redux/actions/teacherActions";
import { getSubjects } from "../redux/actions/subjectActions";
import { TimetableSlotResponseDto } from "../shared/dtos/timetableSlot/TimetableSlotResponseDto";
import {
  DAYS_OF_WEEK,
  DAY_OF_WEEK_OPTIONS,
} from "../shared/data/calendarConstants";

const TimetableSlotsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    timetableSlots,
    loadingTimetableSlots,
    errorLoadingTimetableSlots,
    serverStatus,
    loadingTimetableSlotUpdate,
    errorUpdatingTimetableSlot,
  } = useAppSelector((state) => state.timetableSlot);

  const { timetables } = useAppSelector((state) => state.timetable);
  const { items: teachers } = useAppSelector((state) => state.teacher);
  const { items: subjects } = useAppSelector((state) => state.subject);

  // Filters State
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [selectedTimetableId, setSelectedTimetableId] = useState<string>("");
  const [selectedTeacherId, setSelectedTeacherId] = useState<string>("");
  const [selectedSubjectId, setSelectedSubjectId] = useState<string>("");
  const [selectedDay, setSelectedDay] = useState<string>("");

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 150);

  // Modal State
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [selectedSlot, setSelectedSlot] =
    useState<TimetableSlotResponseDto | null>(null);

  const canUpdate = hasPermission("timetable_slot:update");

  // Load Lookups
  useEffect(() => {
    if (timetables.length === 0) dispatch(getTimetables());
    if (teachers.length === 0) dispatch(getTeachers());
    if (subjects.length === 0) dispatch(getSubjects());
  }, [dispatch, timetables.length, teachers.length, subjects.length]);

  const fetchSlotsList = useCallback((): void => {
    dispatch(
      getTimetableSlots({
        branchId: selectedBranchId || undefined,
        timetableId: selectedTimetableId || undefined,
        teacherId: selectedTeacherId || undefined,
        subjectId: selectedSubjectId || undefined,
        dayOfWeek: selectedDay || undefined,
      }),
    );
  }, [
    dispatch,
    selectedBranchId,
    selectedTimetableId,
    selectedTeacherId,
    selectedSubjectId,
    selectedDay,
  ]);

  useEffect(() => {
    fetchSlotsList();
  }, [fetchSlotsList]);

  // Options
  const timetableOptions = useMemo(
    () =>
      timetables.map((t) => ({
        value: t.id,
        label: `${t.name} (${t.classroomName})`,
      })),
    [timetables],
  );

  // Client-side search and sort
  const filteredSlots = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase();

    return timetableSlots
      .filter((slot) => {
        if (!query) return true;
        const matchesSubject = slot.subjectName?.toLowerCase().includes(query);
        const matchesTeacher = slot.teacherName?.toLowerCase().includes(query);
        const matchesRoom = slot.location?.toLowerCase().includes(query);
        return Boolean(matchesSubject || matchesTeacher || matchesRoom);
      })
      .sort((a, b) => {
        const dayDiff =
          DAYS_OF_WEEK.indexOf(a.dayOfWeek as any) -
          DAYS_OF_WEEK.indexOf(b.dayOfWeek as any);
        if (dayDiff !== 0) return dayDiff;
        const startA = a.startTime ? String(a.startTime) : "";
        const startB = b.startTime ? String(b.startTime) : "";
        return startA.localeCompare(startB);
      });
  }, [debouncedSearch, timetableSlots]);

  const handleOpenUpdate = (slot: TimetableSlotResponseDto): void => {
    setSelectedSlot(slot);
    setIsUpdateModalOpen(true);
  };

  const handleCloseUpdate = useCallback((): void => {
    if (!loadingTimetableSlotUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedSlot(null);
    }
  }, [loadingTimetableSlotUpdate]);

  useEffect(() => {
    if (!serverStatus) return;
    if (serverStatus.operation === "update" && serverStatus.status === 200)
      handleCloseUpdate();
    dispatch(resetTimetableSlotServerStatus());
  }, [serverStatus, dispatch, handleCloseUpdate]);

  return (
    <PageContainer
      title="Timetable Lessons"
      subtitle="Modify times, days, locations, and teachers for scheduled class sessions."
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search lessons..."
      >
        <BranchSelectFilter
          value={selectedBranchId}
          onChange={setSelectedBranchId}
        />

        <AppSelectFilter
          label="Timetable"
          value={selectedTimetableId}
          onChange={setSelectedTimetableId}
          options={timetableOptions}
          allLabel="All Timetables"
          minWidth={160}
        />

        <TeacherSelectFilter
          value={selectedTeacherId}
          branchId={selectedBranchId}
          onChange={setSelectedTeacherId}
        />

        <SubjectSelectFilter
          value={selectedSubjectId}
          onChange={setSelectedSubjectId}
        />

        <AppSelectFilter
          label="Day"
          value={selectedDay}
          onChange={setSelectedDay}
          options={DAY_OF_WEEK_OPTIONS}
          allLabel="All Days"
          minWidth={120}
        />
      </PageToolbar>

      <VirtualizedCardGrid
        data={filteredSlots}
        loading={loadingTimetableSlots}
        error={errorLoadingTimetableSlots}
        emptyMessage="No timetable lessons match your filter criteria"
        onRetry={fetchSlotsList}
        renderCard={(slot) => (
          <TimetableSlotCard
            key={slot.id}
            slot={slot}
            onEdit={canUpdate ? () => handleOpenUpdate(slot) : undefined}
          />
        )}
      />

      {isUpdateModalOpen && selectedSlot && (
        <UpdateTimetableSlotModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          slot={selectedSlot}
          loading={loadingTimetableSlotUpdate}
          error={errorUpdatingTimetableSlot}
        />
      )}
    </PageContainer>
  );
};

export default TimetableSlotsPage;
